import { ChangeDetectorRef, Component } from '@angular/core';
import { NgFor, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { finalize } from 'rxjs/operators';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProyectosService } from 'src/app/proyectos/proyectos.service';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Proyecto } from 'src/app/shared/model/gestion-proyectos/proyecto';
import { ConsultaEpicasComponent } from '../../consulta-epicas/consulta-epicas.component';
import { ConsultaSprintComponent } from '../../consulta-sprint/consulta-sprint.component';

@Component({
  selector: 'app-consulta-hu-cux-proyecto',
  templateUrl: './consulta-hu-cux-proyecto.component.html',
  imports: [
    NgIf,
    NgFor,
    FormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    ConsultaEpicasComponent,
    ConsultaSprintComponent,
  ],
})
export class ConsultaHuCuxProyectoComponent {
  requestFilter = new ConsultaList<Proyecto>();
  projects: Proyecto[] = [];
  nomProyecto = '';
  totalRegistros = 0;
  currentPage = 1;
  pageSize = 5;
  readonly pageSizeOptions = [5, 10, 20];
  loading = false;

  mostrarActividades = false;
  mostrarSprint = false;
  mostrarPrincipal = false;
  proyecto: Proyecto = new Proyecto();
  titulo = '';

  constructor(
    private proyectoService: ProyectosService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.titulo = 'Mis Proyectos';
    this.consultaMisProyectos();
  }

  consultaMisProyectos(page = 1): void {
    this.mostrarPrincipal = true;
    this.currentPage = Math.max(page, 1);
    this.loading = this.projects.length === 0;
    this.cdr.detectChanges();

    this.requestFilter.param1 = this.nomProyecto?.trim() ?? '';
    this.requestFilter.param6 = 2;
    this.requestFilter.regInicial = (this.currentPage - 1) * this.pageSize;
    this.requestFilter.regXPagina = this.pageSize;
    this.requestFilter.pagina = this.currentPage - 1;

    this.proyectoService
      .consultaProyectoMongo(this.requestFilter)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        }),
      )
      .subscribe((response: any) => {
        this.projects = Array.isArray(response?.content) ? [...response.content] : [];
        this.totalRegistros = this.resolveTotalRegistros(response, this.projects.length);

        const maxPages = this.totalPages;
        if (maxPages > 0 && this.currentPage > maxPages) {
          this.consultaMisProyectos(maxPages);
          return;
        }

        this.cdr.detectChanges();
      });
  }

  consultarActividades(proyecto: Proyecto): void {
    this.proyecto = proyecto;
    this.mostrarActividades = true;
    this.mostrarSprint = false;
    this.mostrarPrincipal = false;
    this.titulo = 'Mis Actividades';
  }

  consultaSprint(proyecto: Proyecto): void {
    this.proyecto = proyecto;
    this.mostrarSprint = true;
    this.mostrarActividades = false;
    this.mostrarPrincipal = false;
    this.titulo = 'Sprints del Proyecto';
  }

  mostrarLista(): void {
    this.proyecto = new Proyecto();
    this.mostrarActividades = false;
    this.mostrarSprint = false;
    this.mostrarPrincipal = true;
    this.titulo = 'Mis Proyectos';
    this.consultaMisProyectos(this.currentPage);
  }

  clearNombrePro(): void {
    this.nomProyecto = '';
    this.consultaMisProyectos(1);
  }

  onPageSizeChange(value: number): void {
    this.pageSize = Number(value);
    this.consultaMisProyectos(1);
  }

  onPageChange(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.totalPages) {
      return;
    }
    this.consultaMisProyectos(page);
  }

  firstPage(): void {
    this.onPageChange(1);
  }

  previousPage(): void {
    this.onPageChange(this.currentPage - 1);
  }

  nextPage(): void {
    this.onPageChange(this.currentPage + 1);
  }

  lastPage(): void {
    this.onPageChange(this.totalPages);
  }

  get totalPages(): number {
    return this.totalRegistros > 0 ? Math.ceil(this.totalRegistros / this.pageSize) : 0;
  }

  get showPagination(): boolean {
    return this.totalRegistros > 0;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.totalRegistros);
  }

  getProjectIdentifier(proyecto: Proyecto): string {
    return proyecto?.idProyecto || proyecto?.id?.toString() || '-';
  }

  getProjectShortName(proyecto: Proyecto): string {
    return proyecto?.nombre_corto || 'Sin nombre corto';
  }

  getDateRangeLabel(proyecto: Proyecto): string {
    const start = this.formatDate(proyecto?.fechaInicio);
    const end = this.formatDate(proyecto?.fechaFin);

    if (!start && !end) {
      return 'Sin fechas definidas';
    }

    if (!start || !end) {
      return `${start || 'Sin inicio'} / ${end || 'Sin fin'}`;
    }

    return `${start} / ${end}`;
  }

  getPaginationItems(): Array<number | string> {
    if (this.totalPages <= 1) {
      return [1];
    }

    const pages = new Set<number>([
      1,
      this.totalPages,
      this.currentPage - 1,
      this.currentPage,
      this.currentPage + 1,
    ]);

    const sortedPages = Array.from(pages)
      .filter((page) => page >= 1 && page <= this.totalPages)
      .sort((a, b) => a - b);

    const items: Array<number | string> = [];
    for (let index = 0; index < sortedPages.length; index++) {
      const page = sortedPages[index];
      const previous = sortedPages[index - 1];
      if (previous && page - previous > 1) {
        items.push(`ellipsis-${previous}-${page}`);
      }
      items.push(page);
    }

    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  formatDate(date: Date | string): string {
    if (!date) {
      return '';
    }

    const parsedDate = typeof date === 'string' ? new Date(date) : date;
    if (parsedDate instanceof Date && !Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toLocaleDateString();
    }

    return '';
  }

  private resolveTotalRegistros(response: any, currentItems: number): number {
    const candidates = [
      response?.totalElements,
      response?.pTotalItems,
      response?.totalItems,
      response?.totalRegistros,
    ];

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed) && parsed >= 0) {
        return parsed;
      }
    }

    if (currentItems === 0) {
      return 0;
    }

    return (this.currentPage - 1) * this.pageSize + currentItems;
  }
}
